import React, { useEffect, useState } from "react";
import HiddenDataTable from "../../../../../../components/HiddenDataTable";
import style from "./style.module.scss";
import ButtonIcon from "../../../../../../components/ButtonIcon/ButtonIcon";
import Filter from "../../../../../../assets/svg/icons/common/Filter";
import Download from "../../../../../../assets/svg/icons/common/Download";
import BatchDataTable from "./BatchDataTable/Index";
import { getMerchantBatchData, getMerchantBatchDataExport } from "../../../../../../../api";
import MonthPicker from "../../../../../../components/MonthPicker/Index";
import { getFullMonthName } from "../../../../../../../components/helper/helper";
import useMerchantStore from "../../../../../../stores/merchant";
import useCommonStore from "../../../../../../stores/common";
import Pagination from "../../../../../../components/Pagination/Pagination";
import toast from "react-hot-toast";

function slugifyCsvSegment(value, fallback, maxLen) {
  const raw = String(value ?? "").trim() || String(fallback);
  const slug = raw
    .replace(/[^A-Za-z0-9._-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|[.-]+$/g, "")
    .toLowerCase()
    .slice(0, maxLen);
  return slug || String(fallback).toLowerCase().slice(0, maxLen);
}

function BatchData() {
  const expandDetails = useCommonStore((state) => state.expandDetails);
  const activeApp = useMerchantStore((state) => state.activeApp);
  const [batchData, setBatchData] = useState([]);
  const [batchTotals, setBatchTotals] = useState(null);
  const [year, setYear] = useState(null);
  const [month, setMonth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [downloadingRowId, setDownloadingRowId] = useState(null);
  const dateRange = useCommonStore((state) => state.dateRange);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 10,
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getMerchantBatchData({
        powerapp_id: activeApp?.id,
        start_date: dateRange.startDate,
        end_date: dateRange.endDate,
        page: pagination.currentPage,
      });

      if (response?.status && response?.data) {
        const { data, current_page, last_page, total, per_page, totals } = response.data;

        setBatchData(Array.isArray(data) ? data : []);
        setBatchTotals(totals ?? null);

        setPagination({
          currentPage: current_page,
          totalPages: last_page,
          totalItems: total,
          perPage: per_page,
        });
      } else {
        setBatchData([]);
        setBatchTotals(null);
      }
    } catch (error) {
      console.error(error);
      setBatchData([]);
      setBatchTotals(null);
      toast.error("Failed to load batch data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [activeApp?.id, dateRange, pagination.currentPage]);

  const onChangeFilter = (data) => {
    setYear(data.year);
    setMonth(getFullMonthName(data.month));
  };

  const onExportClickHandler = async () => {
    setIsExporting(true);
    try {
      const response = await getMerchantBatchDataExport({
        powerapp_id: activeApp?.id,
        start_date: dateRange.startDate,
        end_date: dateRange.endDate,
        export: true,
      });
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const date = new Date(dateRange.startDate);
      const monthNames = [
        "jan",
        "feb",
        "mar",
        "apr",
        "may",
        "jun",
        "jul",
        "aug",
        "sept",
        "oct",
        "nov",
        "dec",
      ];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      const fileName = `batch-data-${month}-${year}.csv`;

      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("CSV downloaded successfully");
    } catch (error) {
      console.log({ error });
      toast.error("Failed to download CSV");
    }
    setIsExporting(false);
  };

  const onRowDownload = async (row) => {
    if (downloadingRowId !== null) {
      return;
    }

    setDownloadingRowId(row.id);
    try {
      const response = await getMerchantBatchDataExport({
        powerapp_id: activeApp?.id,
        start_date: dateRange.startDate,
        end_date: dateRange.endDate,
        export: true,
        batch_id: row.id,
      });
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const dbaSlug = slugifyCsvSegment(activeApp?.field_dba, "merchant", 60);
      const batchSlug = slugifyCsvSegment(row?.batch_number, `batch-${row.id}`, 40);
      const dateStr =
        row?.batch_date != null ? String(row.batch_date).slice(0, 10) : "export";
      const fileName = `${dbaSlug}-batch-${batchSlug}-${dateStr}.csv`;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded successfully");
    } catch (error) {
      console.log({ error });
      toast.error("Failed to download CSV");
    }
    setDownloadingRowId(null);
  };

  return (
    <>
      {!expandDetails && (
        <div className={style.container}>
          <HiddenDataTable />
        </div>
      )}

      {expandDetails && (
        <div className={style.container}>
          <header className={style.header}>
            <div className={style.left}>
              <h5>Batch Data</h5>
              {/* <ButtonIcon textColor="#254FE2">Expand All</ButtonIcon> */}
            </div>
            <div className={style.right}>
              {/* <MonthPicker
                text={<Filter />}
                submitText="Apply"
                onSubmit={(data) => onChangeFilter(data)}
              /> */}

              <ButtonIcon 
                bgColor={"#EDEDF4"} 
                onClick={onExportClickHandler}
                disabled={isExporting}
              >
                <Download />
                <span>{isExporting ? "Downloading..." : "Download"}</span>
              </ButtonIcon>
            </div>
          </header>

          <div className={style.body}>
            <BatchDataTable
              data={batchData}
              loading={loading}
              totals={batchTotals}
              onRowDownload={onRowDownload}
              downloadingRowId={downloadingRowId}
              isAnyDownloading={downloadingRowId !== null}
            />
          </div>

          <footer className={style.footer}>
            {pagination.totalPages > 1 && (
              <Pagination
                pageCount={pagination.totalPages}
                currentPage={pagination.currentPage - 1}
                onPageChange={({ selected }) => {
                  setPagination((prev) => ({
                    ...prev,
                    currentPage: selected + 1,
                  }));
                }}
                totalItems={pagination.totalItems}
                itemsPerPage={pagination.perPage}
              />
            )}
          </footer>
        </div>
      )}
    </>
  );
}

export default BatchData;
